let animationFrame = 0;
let cancelUserInterrupt: (() => void) | null = null;

function snapToTop(): void {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); //standard behavior
  document.documentElement.scrollTop = 0; // chrome specific
  document.body.scrollTop = 0; // safari specific
}

/**
 * Scroll the window to the document top.
 *
 * Native `behavior: 'smooth'` is aborted when a window-virtualized list
 * remounts rows and changes `scrollHeight` (PLP). Drive the scroll ourselves
 * with `behavior: 'auto'` so the virtualizer can keep up, then snap to 0.
 */
export function scrollDocumentToTop(): void {
  if (typeof window === 'undefined') return;

  cancelAnimationFrame(animationFrame);
  cancelUserInterrupt?.();
  cancelUserInterrupt = null;

  const startY = window.scrollY || document.documentElement.scrollTop || 0;
  if (startY <= 1) {
    snapToTop();
    return;
  }

  let interrupted = false;
  const interrupt = () => {
    interrupted = true;
  };

  // Add event listeners for wheel and touchstart events when user interrupts the scroll
  window.addEventListener('wheel', interrupt, { passive: true });
  window.addEventListener('touchstart', interrupt, { passive: true });
  cancelUserInterrupt = () => {
    window.removeEventListener('wheel', interrupt);
    window.removeEventListener('touchstart', interrupt);
  };

  // Start the scroll animation
  const start = performance.now();
  const duration = Math.min(800, Math.max(320, startY * 0.35));

  // Tick function to update the scroll position
  const tick = (now: number) => {
    if (interrupted) {
      cancelUserInterrupt?.();
      cancelUserInterrupt = null;
      return;
    }

    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - (1 - t) ** 3;
    window.scrollTo({ top: startY * (1 - eased), left: 0, behavior: 'auto' });

    if (t < 1) {
      animationFrame = requestAnimationFrame(tick);
      return;
    }

    snapToTop();
    requestAnimationFrame(() => {
      snapToTop();
      requestAnimationFrame(snapToTop);
    });
    cancelUserInterrupt?.();
    cancelUserInterrupt = null;
  };

  animationFrame = requestAnimationFrame(tick);
}
